// CreateHoldInvoice.tsx (cloned from SetReceiveAmount.tsx)
// zz-support-hold-invoices-fable.txt 10.4
import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconHourglass } from "@tabler/icons-react";

import { BOB_MODALS } from "../../helpers/constants";
import { Bob } from "../../stores/Bob";
import UserCreatedInvoiceQR from "./helpers/UserCreatedInvoiceQR";
import { NumericKeypad } from "../details/numericKeypad";
import { formatDurationSeconds } from "../../helpers/holdInvoices";
import { CustomModalTitle } from "../details/customModalTitle";

const EXPIRY_CHOICES = [
  { value: "3600", label: "1 hour" },
  { value: "21600", label: "6 hours" },
  { value: "86400", label: "24 hours" },
];

const CreateHoldInvoice = observer(({ bob }: { bob: Bob }) => {
  const settings = bob.workspace.holdInvoiceSettings?.data;
  const [amount, setAmount] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const [expirySeconds, setExpirySeconds] = useState<string>("86400");

  if (!settings) return null;

  const expiryChoices = EXPIRY_CHOICES.filter(
    (choice) =>
      Number(choice.value) >= settings.min_expiry_seconds &&
      Number(choice.value) <= settings.max_expiry_seconds,
  );
  const minSats = settings.min_amount_sats;
  const maxSats = Math.min(settings.max_amount_sats, bob.workspace.maxInvoiceSatoshis);
  const isAmountTooLow = typeof amount === "number" && amount > 0 && amount < minSats;
  const isAmountTooHigh = typeof amount === "number" && amount > maxSats;
  const isValidAmount =
    typeof amount === "number" && amount >= minSats && amount <= maxSats;
  const isInvoiceReady = !!bob.createdHoldInvoicePayRequest;

  const handleClose = () => {
    bob.setOpenModal(null);
    bob.setCreatedHoldInvoicePayRequest(null);
    bob.setHoldCreateError(null);
  };

  const handleChangeAmount = (value: number | "") => {
    if (bob.holdCreateError) bob.setHoldCreateError(null);
    setAmount(value);
  };

  const handleCreate = () => {
    if (!isValidAmount) return;
    bob.createHoldInvoice(amount as number, description.trim(), Number(expirySeconds));
  };

  return (
    <Modal
      opened={bob.openModal === BOB_MODALS.CREATE_HOLD_INVOICE}
      onClose={handleClose}
      title={
        <CustomModalTitle
          text={isInvoiceReady ? "Hold invoice created" : "Create hold invoice"}
          icon={<IconHourglass />}
        />
      }
    >
      <Paper withBorder shadow="md" p={24} radius="md">
        <Stack>
          {!isInvoiceReady && (
            <>
              <NumericKeypad
                currencyCode={bob.currencyCode}
                currencySnapshot={bob.workspace.exchangeRates.data}
                initialMode="sats"
                valueSats={typeof amount === "number" ? amount : 0}
                minSats={minSats}
                maxSats={maxSats}
                error={
                  isAmountTooLow
                    ? `minimum ${minSats.toLocaleString()} satoshis`
                    : isAmountTooHigh
                      ? `maximum ${maxSats.toLocaleString()} satoshis`
                      : undefined
                }
                onChangeSats={handleChangeAmount}
              />

              <TextInput
                label="Invoice comments"
                placeholder="Optional note (max 100 chars)"
                description="Appears in the memo/description field of the invoice."
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                maxLength={100}
              />

              <Select
                label="Invoice expires after"
                description="How long the payer has to pay. Once paid, the decision time below applies."
                data={expiryChoices}
                value={expirySeconds}
                onChange={(value) => value && setExpirySeconds(value)}
                allowDeselect={false}
              />

              <Text fz="sm" c="dimmed">
                You'll have{" "}
                {formatDurationSeconds(settings.decision_window_seconds)} to
                accept or reject once paid. If you do nothing, we reject it and
                the payer is refunded.
              </Text>

              {bob.holdCreateError && (
                <Alert variant="light" color="red">
                  {bob.holdCreateError}
                </Alert>
              )}

              <Group justify="flex-end" mt="md">
                <Button variant="outline" onClick={handleClose}>
                  Exit
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={bob.areWeWaitingOnRails || !isValidAmount}
                  loading={bob.areWeWaitingOnRails}
                >
                  Create hold invoice
                </Button>
              </Group>
            </>
          )}

          {isInvoiceReady && (
            <>
              {typeof amount === "number" && (
                <Stack gap={4} align="center" mt="md">
                  <Text size="lg" fw={700}>
                    {amount.toLocaleString()} sats
                  </Text>
                  <Text size="sm" c="dimmed">
                    {bob.formatFiat(amount)}
                  </Text>
                </Stack>
              )}
              <UserCreatedInvoiceQR
                bob={bob}
                invoice={bob.createdHoldInvoicePayRequest}
              />
              <Text fz="sm" c="dimmed" ta="center">
                Track it under Receive → Hold Invoices. When it is paid, you
                will be asked to accept or reject the payment.
              </Text>
              <Group justify="flex-end" mt="md">
                <Button variant="outline" onClick={handleClose}>
                  Exit
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Paper>
    </Modal>
  );
});

export default CreateHoldInvoice;
